import { GetBusinessFrozenImprovementsResponse } from "@1games-ru/region-shared/dist/backend/requests/business";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import { infoSelector, pcInfoSelector, setInfo, setPcInfo } from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useGetFrozenImprovements(businessId: number, device: DeviceEnum) {
  const { t, i18n } = useTranslation("business");
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [improvementsInfo, setImprovementsInfo] =
    useState<GetBusinessFrozenImprovementsResponse | null>(null);
  const [isAcceptLoading, setIsAcceptLoading] = useState(false);
  const [isDeclineLoading, setIsDeclineLoading] = useState(false);

  const tabletInfo = useAppSelector(infoSelector);
  const pcInfo = useAppSelector(pcInfoSelector);
  const info = device === DeviceEnum.TABLET ? tabletInfo : pcInfo;

  const load = useCallback(async () => {
    if (businessId) {
      setIsLoading(true);

      try {
        const response = await BusinessMpProvider.getFrozenImprovements(businessId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          setImprovementsInfo(response.data);
        } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
          console.error(response.error.message);
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response.error.message);
          toast.error(t("business.toast.pricesForServicesNotLoaded"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.pricesForServicesNotLoaded"));
      } finally {
        setIsLoading(false);
      }
    }
  }, [businessId, i18n, t]);

  useEffect(() => {
    load();
  }, [load]);

  const acceptImprovements = useCallback(async () => {
    if (businessId) {
      setIsAcceptLoading(true);

      try {
        const response = await BusinessMpProvider.acceptFrozenImprovements(businessId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          toast.success(t("business.toast.freezeImprovementsAccepted"));
          if (device === DeviceEnum.TABLET) {
            dispatch(setInfo({ ...info, hasFrozenImprovements: false }));
          } else {
            dispatch(setPcInfo({ ...info, hasFrozenImprovements: false }));
          }
        } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
          console.error(response.error.message);
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response.error.message);
          toast.error(t("business.toast.freezeImprovementsAcceptError"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.freezeImprovementsAcceptError"));
      } finally {
        setIsAcceptLoading(false);
      }
    }
  }, [businessId, device, dispatch, i18n, info, t]);

  const declineImprovements = useCallback(async () => {
    if (businessId) {
      setIsDeclineLoading(true);

      try {
        const response = await BusinessMpProvider.declineFrozenImprovements(businessId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          toast.success(t("business.toast.freezeImprovementsDeclined"));
          if (device === DeviceEnum.TABLET) {
            dispatch(setInfo({ ...info, hasFrozenImprovements: false }));
          } else {
            dispatch(setPcInfo({ ...info, hasFrozenImprovements: false }));
          }
        } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
          console.error(response.error.message);
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response.error.message);
          toast.error(t("business.toast.freezeImprovementsDeclineError"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.freezeImprovementsDeclineError"));
      } finally {
        setIsDeclineLoading(false);
      }
    }
  }, [businessId, device, dispatch, i18n, info, t]);

  return {
    isLoading,
    improvementsInfo,
    isAcceptLoading,
    acceptImprovements,
    isDeclineLoading,
    declineImprovements,
  };
}
